/**
 * Grade de fotos com upload, remoção e REORDENAÇÃO.
 *
 * A ordem é o que o site publica: a primeira foto é a capa (card da listagem,
 * imagem grande da galeria e a que aparece quando o link é compartilhado), e as
 * demais seguem na sequência.
 *
 * Dois jeitos de reordenar, de propósito: arrastar (natural no computador) e os
 * botões ◀ ▶ e a estrela (o arrastar nativo do navegador NÃO funciona em tela de
 * toque — sem os botões, quem usa celular ou tablet simplesmente não conseguiria).
 *
 * Era um componente copiado em PropertyFormPage e ProductFormPage; virou peça
 * única para as duas telas não divergirem de novo.
 */

import React, { useCallback, useState } from 'react';

import { api } from '@/lib/api';
import { FileDropzone } from '@/shared/components/ui/FileDropzone';
import { Icon } from '@/shared/icons';

interface PhotoUploaderProps {
  photos: string[];
  /** `reordenadoAMao` só vem `true` quando a PESSOA mexeu na ordem — nunca em
   *  upload ou remoção. É o que a tela usa para marcar `media.photosManualOrder`
   *  e fazer a ordem sobreviver à sincronização com o sistema externo. */
  onChange: (photos: string[], opcoes?: { reordenadoAMao?: boolean }) => void;
  /** Pasta do upload no servidor: 'properties' | 'products'. */
  folder: string;
  compact?: boolean;
  idPrefix?: string;
}

export const PhotoUploader: React.FC<PhotoUploaderProps> = ({
  photos,
  onChange,
  folder,
  compact = false,
  idPrefix = 'photo',
}) => {
  const [uploading, setUploading] = useState(false);
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [alvo, setAlvo] = useState<number | null>(null);

  const uploadFile = useCallback(async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`uploads?folder=${folder}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data.url as string;
  }, [folder]);

  const handleFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setUploading(true);
    try {
      const urls: string[] = [];
      for (const file of files) {
        const url = await uploadFile(file);
        urls.push(url);
      }
      onChange([...photos, ...urls]);
    } catch {
      // Error handled by API interceptor
    } finally {
      setUploading(false);
    }
  }, [photos, onChange, uploadFile]);

  const removePhoto = (index: number) => {
    onChange(photos.filter((_, i) => i !== index));
  };

  /** Tira a foto de `de` e a enfia em `para`, empurrando as outras. */
  const mover = (de: number, para: number) => {
    if (de === para || para < 0 || para >= photos.length) return;
    const nova = [...photos];
    const [movida] = nova.splice(de, 1);
    if (movida === undefined) return;
    nova.splice(para, 0, movida);
    onChange(nova, { reordenadoAMao: true });
  };

  const finalizarArrasto = () => {
    setArrastando(null);
    setAlvo(null);
  };

  const gridCols = compact
    ? 'grid-cols-3'
    : 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6';

  return (
    <div className="space-y-3">
      {photos.length > 0 && (
        <>
          <div className={`grid ${gridCols} gap-2`}>
            {photos.map((url, index) => (
              <div
                key={`${url}-${index}`}
                draggable
                onDragStart={(e) => {
                  setArrastando(index);
                  e.dataTransfer.effectAllowed = 'move';
                  // Alguns navegadores só iniciam o arrasto se houver dado no evento.
                  e.dataTransfer.setData('text/plain', String(index));
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (alvo !== index) setAlvo(index);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (arrastando !== null) mover(arrastando, index);
                  finalizarArrasto();
                }}
                onDragEnd={finalizarArrasto}
                className="relative group rounded-lg overflow-hidden border aspect-square"
                style={{
                  borderColor: alvo === index && arrastando !== null && arrastando !== index
                    ? 'var(--theme-primary)'
                    : 'var(--theme-card-border)',
                  borderWidth: alvo === index && arrastando !== null && arrastando !== index ? 2 : 1,
                  opacity: arrastando === index ? 0.4 : 1,
                  cursor: 'grab',
                }}
                data-testid={`${idPrefix}-photo-${index}`}
              >
                <img src={url} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />

                {/* O crachá ocupa o MESMO canto da estrela das outras fotos: no
                    lugar de "clique para virar a principal" lê-se "esta já é". Ficava
                    embaixo no meio e encostava na seta em miniatura estreita. */}
                {index === 0 && (
                  <span
                    className="absolute top-1 left-1 text-[8px] font-bold px-2 py-1 rounded-full whitespace-nowrap pointer-events-none"
                    style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#ffffff' }}
                  >
                    principal
                  </span>
                )}

                {/* Estrela = "tornar principal". Fica no canto OPOSTO ao X e só
                    aparece nas fotos que ainda não são a capa — antes era um botão
                    escrito "principal", igualzinho ao crachá da primeira foto, e no
                    celular era impossível saber qual das duas era a capa de verdade. */}
                {index !== 0 && (
                  <button
                    type="button"
                    onClick={() => mover(index, 0)}
                    aria-label={`Tornar a foto ${index + 1} a principal`}
                    className="absolute top-1 left-1 p-1.5 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#ffffff' }}
                    data-testid={`${idPrefix}-photo-cover-${index}`}
                  >
                    <Icon name="star" className="w-3 h-3" />
                  </button>
                )}

                {/* Setas de ordem: sempre visíveis no toque (onde não há hover nem
                    arrasto) e reveladas no hover em telas com ponteiro. Ficam nas
                    pontas de baixo, deixando o meio livre para o crachá. Seta sem
                    função fica `invisible` (não apagada): some da vista mas guarda o
                    lugar, para a que sobra não pular de canto nem encavalar o crachá. */}
                <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between gap-1 pointer-events-none opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => mover(index, index - 1)}
                    disabled={index === 0}
                    aria-label={`Mover a foto ${index + 1} para trás`}
                    className="p-1.5 rounded-full pointer-events-auto disabled:invisible"
                    style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#ffffff' }}
                    data-testid={`${idPrefix}-photo-back-${index}`}
                  >
                    <Icon name="chevron-left" className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => mover(index, index + 1)}
                    disabled={index === photos.length - 1}
                    aria-label={`Mover a foto ${index + 1} para frente`}
                    className="p-1.5 rounded-full pointer-events-auto disabled:invisible"
                    style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#ffffff' }}
                    data-testid={`${idPrefix}-photo-forward-${index}`}
                  >
                    <Icon name="chevron-right" className="w-3 h-3" />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  aria-label={`Remover a foto ${index + 1}`}
                  className="absolute top-1 right-1 p-1 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#ffffff' }}
                  data-testid={`${idPrefix}-photo-remove-${index}`}
                >
                  <Icon name="x" className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          {photos.length > 1 && (
            <p className="text-xs" style={{ color: 'var(--theme-text-secondary)' }}>
              Arraste para reordenar. A primeira foto é a que aparece no site como capa.
            </p>
          )}
        </>
      )}

      {/* Dropzone shared (F84) */}
      <FileDropzone
        accept="image/*"
        multiple
        onFiles={(files) => void handleFiles(files)}
        uploading={uploading}
        compact={compact || photos.length > 0}
        icon={photos.length === 0 ? 'image' : 'upload'}
        label={photos.length === 0 ? 'Clique ou arraste imagens' : 'Adicionar mais'}
        hint="JPG, PNG, WebP"
        data-testid={`${idPrefix}-photo-dropzone`}
      />
    </div>
  );
};
